// Services
import API from 'src/api'
import { OfflineStorage } from 'src/services/offline-storage'
import { isReallyOnline } from 'src/services/connectivity'
// Offline
import { StorageAdapter } from './storage/StorageAdapter'
import { drainLegacyAnswers, listRealPendingLegacyAnswers } from './storage/legacy-answers'
import { closeOccurrence } from './domain/occurrences'
import { getTransport } from './sync/transport'
import { is2xx, throwFromResponse } from './sync/http-helpers'

/**
 * pending-close.js: RED DE SEGURIDAD del cierre de auditoria cuando NO queda ningun camino vivo.
 *
 * EL AGUJERO QUE TAPA: en Android el plugin @capacitor-community/sqlite puede perder su llave interna
 * en el keystore del sistema. El open() falla, StorageAdapter._backend queda en null y toda escritura
 * a SQLite lanza -> el cierre offline-first (outbox) no existe. Si ADEMAS no hay red, el cierre online
 * legacy tampoco existe: el usuario termina de llenar la auditoria y no la puede cerrar por NINGUNA
 * via. Aqui se persiste la INTENCION de cierre en OfflineStorage (JSON en disco / localStorage), que
 * es 100% independiente de SQLite y por eso sobrevive a ese modo de fallo, y se reproduce despues.
 *
 * NO BORRA NADA: no toca la base SQLite, ni el outbox, ni las respuestas offline (donde viven las
 * fotos aun sin subir). Solo agrega y da de baja sus propias filas de pending_closes.json.
 *
 * ORDEN DE REPRODUCCION (por registro, el primero que aplique):
 *   1. SQLite volvio a abrir -> se devuelve el cierre a la maquinaria de siempre (EVENT
 *      occurrence.close en el outbox). Desde ahi la idempotencia ya es la del drain
 *      (pre-check de la marca `occ-closed:<occId>` en meta). NO hace falta red.
 *   2. SQLite sigue caido pero hay red REAL -> cierre online directo (mismo endpoint save-and-close
 *      que el cierre legacy), con el MISMO criterio de idempotencia traducido a esta capa: se
 *      PREGUNTA primero al servidor si la auditoria ya esta cerrada y solo se POSTea si no lo esta.
 *   3. Ni base ni red -> el registro se queda pendiente, intacto, para el proximo intento.
 *
 * IDEMPOTENCIA (por que no se cierra dos veces):
 *   - Camino 1: el drain hace `MetaRepository.has(occ-closed:<occId>)` antes de POSTear; un op
 *     duplicado se finaliza sin red. Encolar dos veces es inofensivo.
 *   - Camino 2: pre-check GET occurrences/<id>.is_closed -> si el servidor ya la ve cerrada, el
 *     registro se marca aplicado SIN POSTear. Cubre la ventana "POST exitoso y la app muere antes de
 *     dar de baja el registro".
 *   - Un solo replay a la vez (single-flight de modulo) y los registros se de-duplican por
 *     occurrence_id, asi que dos toques de FINALIZAR no generan dos cierres.
 *   - El registro NUNCA se descarta por un fallo: se le suma un intento y se reintenta despues.
 *
 * SIN `notify`: el cierre offline-first del drain tampoco lo llama. Evita correos duplicados.
 */

const GRAPHQL_URL = API.GRAPHQL_URL

// Cuantos cierres YA aplicados se conservan como lapida (rastro local de "esto ya se cerro", util para
// soporte). Los pendientes NUNCA se recortan: son trabajo del usuario sin entregar.
const MAX_APPLIED_TOMBSTONES = 25

const isPending = (record) => !!record && !record.applied_at

const errorMessage = (error) => String((error && error.message) || error)

/** Header de idempotencia estable por registro (el backend hoy lo ignora; es hardening). */
const idemHeaderFor = (record) => ({
  headers: { 'Idempotency-Key': `pending-close:${record.occurrence_id}:${record.created_at}` }
})

function withDefaults (options) {
  return {
    storage: OfflineStorage,
    db: StorageAdapter,
    transport: getTransport,
    enqueueClose: closeOccurrence,
    isOnline: isReallyOnline,
    hasBackupAnswers: async (occId) => (await listRealPendingLegacyAnswers(occId)).length > 0,
    drainBackupAnswers,
    ...options
  }
}

function drainBackupAnswers (occId, deps) {
  return drainLegacyAnswers({
    getOccurrenceId: () => occId,
    sendBatch: async (formData) => {
      const res = await deps.transport().post(`${GRAPHQL_URL}occurrences/save-offline-answers-copy`, formData)
      if (!is2xx(res)) throwFromResponse(res)
      return res.data
    },
    onBadNetwork () {},
    onSyncStart () {},
    onPartial () {},
    onSyncEnd () {},
    skipBadNetworkCheck: true
  })
}

async function sendBackupAnswersFirst (occId, deps) {
  if (!(await deps.hasBackupAnswers(occId))) return true
  if (!(await deps.isOnline())) return false
  const result = await deps.drainBackupAnswers(occId, deps)
  if (result.failed > 0) throw new Error(`respaldo de respuestas sin subir: ${result.failed} lote(s) fallido(s)`)
  return true
}

/** Conserva TODOS los pendientes y solo las ultimas lapidas aplicadas. */
function trimApplied (records) {
  const applied = records.filter(record => !isPending(record))
  const dropped = new Set(applied.slice(0, Math.max(0, applied.length - MAX_APPLIED_TOMBSTONES)))
  return records.filter(record => !dropped.has(record))
}

async function readRecords (storage) {
  const stored = await storage.getPendingCloses()
  return Array.isArray(stored) ? stored : []
}

/**
 * Persiste la INTENCION de cerrar una auditoria. Se llama cuando el cierre no pudo entregarse ni por
 * SQLite ni por red: a partir de aqui el trabajo del usuario esta durable y el cierre se reproduce solo.
 * De-duplica por occurrence_id (dos toques de FINALIZAR = un solo registro, el ultimo payload gana).
 * @param {object} intent { occurrenceId, payload, tasksEditing }
 * @returns {Promise<object>} el registro persistido.
 */
export async function savePendingClose ({ occurrenceId, payload, tasksEditing = [] } = {}, options = {}) {
  const deps = withDefaults(options)
  if (occurrenceId === undefined || occurrenceId === null || occurrenceId === '') {
    throw new Error('savePendingClose: occurrenceId requerido')
  }
  const occId = String(occurrenceId)
  const record = {
    occurrence_id: occId,
    payload: payload || {},
    tasks_editing: Array.isArray(tasksEditing) ? tasksEditing : [],
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
    applied_at: null
  }
  const records = await readRecords(deps.storage)
  // Se descarta cualquier registro previo de ESTA occurrence (pendiente o lapida): el usuario acaba de
  // finalizarla de nuevo, asi que el registro fresco manda.
  const rest = records.filter(item => String(item.occurrence_id) !== occId)
  await deps.storage.setPendingCloses(trimApplied([...rest, record]))
  return record
}

/**
 * ¿Esta occurrence tiene un cierre pendiente de entregar? Lectura pura (util para dejar la vista en
 * solo-lectura al reingresar cuando SQLite no puede responder por el overlay de siempre).
 * @param {string|number} occurrenceId
 * @returns {Promise<boolean>}
 */
export async function hasDeferredClose (occurrenceId, options = {}) {
  return (await findDeferredClose(occurrenceId, options)) !== null
}

/**
 * Registro del cierre pendiente de esta occurrence, o null si no tiene.
 * @param {string|number} occurrenceId
 * @returns {Promise<object|null>}
 */
export async function findDeferredClose (occurrenceId, options = {}) {
  if (occurrenceId === undefined || occurrenceId === null || occurrenceId === '') return null
  const deps = withDefaults(options)
  const records = await readRecords(deps.storage)
  return records.find(record => isPending(record) && String(record.occurrence_id) === String(occurrenceId)) || null
}

/** Pre-check de idempotencia del camino 2: ¿el servidor ya ve la auditoria cerrada? */
async function isClosedOnServer (occId, deps) {
  const res = await deps.transport().get(`${GRAPHQL_URL}occurrences/${occId}`)
  if (!is2xx(res)) throwFromResponse(res)
  const occurrence = (res.data && res.data.data) || res.data || {}
  return occurrence.is_closed === true || occurrence.is_closed === 1
}

/**
 * Entrega UN registro por el mejor camino disponible.
 * @returns {Promise<string>} 'enqueued' | 'closed' | 'already-closed' | 'deferred'
 */
async function applyOne (record, deps) {
  const occId = String(record.occurrence_id)

  if (!(await sendBackupAnswersFirst(occId, deps))) return 'deferred'

  // Camino 1: la base volvio. Se devuelve el cierre al outbox y la idempotencia pasa a ser la del
  // drain (marca occ-closed:<occId>). Sin respaldo de respuestas no requiere red: el worker drena cuando la haya.
  if (deps.db.isReady()) {
    await deps.enqueueClose(occId, {
      payload: record.payload || {},
      tasksEditing: record.tasks_editing || []
    })
    return 'enqueued'
  }

  // Camino 2: sin base. Solo con red REAL se intenta el cierre online directo.
  if (!(await deps.isOnline())) return 'deferred'

  // Pre-check ANTES de POSTear: si ya esta cerrada (p.ej. la app murio entre un POST exitoso y la baja
  // del registro), no se vuelve a cerrar. Si el GET falla, se propaga: mejor reintentar despues que
  // POSTear a ciegas.
  if (await isClosedOnServer(occId, deps)) return 'already-closed'

  // FIX 9: aquí NO se remapea client_uuid -> server_id a propósito: este camino existe justo porque
  // SQLite no abrió (sin id_mapping ni outbox consultables) y estas tasks nunca encolaron su create.
  // El uuid viaja tal cual y el backend (#1831) las crea completas — única copia del dato. El camino 1
  // (base recuperada) re-encola el cierre al outbox y ahí sí remapea resolveTasksEditingIds al drenar.
  const tasksEditing = record.tasks_editing || []
  if (tasksEditing.length) {
    const tasksRes = await deps.transport().post(
      `${GRAPHQL_URL}tasks/${occId}/occurrenceSave`,
      tasksEditing,
      idemHeaderFor(record)
    )
    if (!is2xx(tasksRes)) throwFromResponse(tasksRes)
  }

  const res = await deps.transport().post(
    `${GRAPHQL_URL}occurrences/${occId}/form-assignments/save-and-close`,
    record.payload || {},
    idemHeaderFor(record)
  )
  if (!is2xx(res)) throwFromResponse(res)
  return 'closed'
}

// Single-flight: el replay se dispara desde varios lados (evento 'online', entrada a la pagina). Dos
// pasadas simultaneas sobre la misma lista podrian cerrar dos veces la misma auditoria.
let inFlightReplay = null

async function runReplay (options) {
  const deps = withDefaults(options)
  const records = await readRecords(deps.storage)
  const pending = records.filter(isPending)
  if (!pending.length) return { applied: 0, pending: 0 }

  let applied = 0
  let touched = false
  for (const record of pending) {
    try {
      const outcome = await applyOne(record, deps)
      if (outcome === 'deferred') continue
      record.applied_at = new Date().toISOString()
      record.outcome = outcome
      applied++
      touched = true
    } catch (error) {
      // Best-effort: el registro se QUEDA pendiente (nunca se descarta) y se reintenta despues.
      record.attempts = (record.attempts || 0) + 1
      record.last_error = errorMessage(error)
      touched = true
      console.error('[pending-close] no se pudo reproducir el cierre pendiente:', error)
    }
  }

  // Una sola escritura al final. Si el proceso muriera antes, los registros siguen pendientes y el
  // proximo replay los reintenta: los pre-checks de arriba impiden el doble cierre.
  if (touched) await deps.storage.setPendingCloses(trimApplied(records))
  return { applied, pending: records.filter(isPending).length }
}

/**
 * Reproduce los cierres pendientes. Idempotente y seguro de llamar cuantas veces haga falta: las
 * llamadas concurrentes comparten la misma pasada y cada registro se pre-chequea antes de aplicarse.
 * Nunca lanza al llamador de UI: los fallos por registro quedan en el propio registro.
 * @returns {Promise<{applied: number, pending: number}>}
 */
export function replayPendingCloses (options = {}) {
  if (inFlightReplay) return inFlightReplay
  inFlightReplay = runReplay(options).finally(() => { inFlightReplay = null })
  return inFlightReplay
}

// Auto-registro del disparador: al volver la red se intenta la reproduccion. Se hace AQUI (y no en
// boot/offline.js) para que este modulo sea autocontenido: quien lo importa obtiene el reintento
// automatico sin cablear nada. El guard de `window` deja el modulo utilizable en tests de node.
let listenerInstalled = false

export function startPendingCloseWatcher () {
  if (listenerInstalled) return
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
  listenerInstalled = true
  window.addEventListener('online', () => {
    replayPendingCloses().catch(error => console.error('[pending-close] replay fallo:', error))
  })
}

startPendingCloseWatcher()
